import React, { useState, useEffect } from 'react';
import { Form, InputGroup } from 'react-bootstrap';
import PropTypes from 'prop-types';

/**
 * ValidatedInput - A reusable form input with built-in validation
 * 
 * This component provides real-time validation and formatting for form inputs,
 * handling various input types such as SSN, phone numbers, email, currency, etc.
 * It works with the validation service to ensure data is properly formatted
 * and displays appropriate error messages.
 * 
 * @param {Object} props - Component props
 * @param {string} props.type - Input type ('text', 'ssn', 'phone', 'email', 'currency', 'zip', 'ein')
 * @param {string} props.value - Current input value
 * @param {function} props.onChange - Function called when input changes
 * @param {string} props.label - Input label text
 * @param {boolean} props.required - Whether input is required
 * @param {string} props.placeholder - Input placeholder text
 * @param {Object} props.validator - Validation object with format, mask, validate functions
 * @param {string} props.name - Input name attribute
 * @param {string} props.id - Input id attribute
 * @param {string} props.className - Additional class name
 * @param {boolean} props.showValidation - Whether to show validation messages
 * @param {boolean} props.validateOnChange - Whether to validate as user types (default: true)
 * @param {number} props.validateAfterLength - Minimum input length before showing validation errors
 * @param {boolean} props.isInvalid - Force input to display as invalid
 * @param {string} props.externalErrorMessage - External error message to override internal validation
 * @returns {JSX.Element} The validated input component
 */
const ValidatedInput = ({
  type = 'text',
  value = '',
  onChange,
  label,
  required = false,
  placeholder,
  validator,
  name,
  id,
  className = '',
  showValidation = true,
  validateOnChange = false,
  validateAfterLength = 1,
  isInvalid = false,
  externalErrorMessage = '',
  ...rest
}) => {
  const [localValue, setLocalValue] = useState(value || '');
  const [validationState, setValidationState] = useState({ isValid: true, message: '' });
  const [touched, setTouched] = useState(false);

  /**
   * Update local value when prop value changes and validate if necessary
   */
  useEffect(() => {
    if (value !== undefined && value !== localValue) {
      setLocalValue(value);
      
      // Always validate if required, regardless of length
      if (required || (validator && validator.validate && value.length >= validateAfterLength)) {
        validateInput(value);
      }
    }
  }, [value]);

  /**
   * Validate the input value using the provided validator
   * 
   * @param {string} valueToValidate - The value to validate
   * @returns {Object} Validation result with isValid and message
   */
  const validateInput = (valueToValidate) => {
    let result = { isValid: true, message: '' };
    
    // Empty check for required fields
    if (required && (!valueToValidate || valueToValidate.trim() === '')) {
      result = { isValid: false, message: 'This field is required' };
    } 
    // Use validator if provided and value is not empty
    else if (
      validator && 
      validator.validate && 
      valueToValidate && 
      valueToValidate.trim() !== ''
    ) {
      result = validator.validate(valueToValidate);
    }
    
    setValidationState(result);
    return result;
  };

  /**
   * Format the input value using the validator's format function, if available
   * 
   * @param {string} valueToFormat - The value to format
   * @returns {string} The formatted value
   */
  const formatValue = (valueToFormat) => {
    if (validator && validator.format && valueToFormat) {
      return validator.format(valueToFormat);
    }
    return valueToFormat;
  };

  /**
   * Handle input change, format the value, and trigger validation
   * 
   * @param {Event} e - The change event
   */
  const handleChange = (e) => {
    const rawValue = e.target.value;
    const formattedValue = formatValue(rawValue);
    
    setLocalValue(formattedValue);
    
    // Validate if validateOnChange is true and value has reached min length
    if (validateOnChange && formattedValue.length >= validateAfterLength) {
      validateInput(formattedValue);
    }
    
    // Call parent onChange with formatted value
    if (onChange) {
      if (typeof onChange === 'function') {
        // Send raw event for regular onchange handlers
        e.target.value = formattedValue;
        onChange(e);
      } else {
        // For custom handlers that expect (name, value) format
        onChange(name, formattedValue);
      }
    }
  };

  /**
   * Handle input blur (when user leaves the field)
   * Always validate on blur if field has been touched
   */
  const handleBlur = () => {
    setTouched(true);
    validateInput(localValue);
  };

  // Determine if we should show validation error
  const showError = (showValidation && touched && !validationState.isValid) || isInvalid;
  
  // Determine the error message to display
  // PRIORITIZE external error message when isInvalid is true
  let errorMessage = '';
  if (isInvalid && externalErrorMessage) {
    // If we have an external error and isInvalid is true, always use the external error
    errorMessage = externalErrorMessage;
  } else if (showValidation && touched && !validationState.isValid) {
    // Otherwise, use our internal validation message if validation failed
    errorMessage = validationState.message || (required ? 'This field is required' : '');
  } else if (isInvalid) {
    // If isInvalid but no external message, use a default message
    errorMessage = 'This field is required';
  }
  
  // Support different input types and stylings
  const isSpecialType = validator && (type === 'ssn' || type === 'phone' || type === 'currency' || type === 'zip' || type === 'ein');

  return (
    <Form.Group className={className} controlId={id || name}>
      {label && (
        <Form.Label>
          {label} {required && <span className="required">*</span>}
        </Form.Label>
      )}
      
      <Form.Control
        type={type}
        name={name}
        value={localValue}
        onChange={handleChange}
        onBlur={handleBlur}
        isInvalid={showError}
        placeholder={placeholder}
        required={required}
        {...rest}
      />
      
      {showError && (
        <Form.Control.Feedback type="invalid">
          {errorMessage}
        </Form.Control.Feedback>
      )}
    </Form.Group>
  );
};

ValidatedInput.propTypes = {
  type: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onChange: PropTypes.func.isRequired,
  label: PropTypes.string,
  required: PropTypes.bool,
  placeholder: PropTypes.string,
  validator: PropTypes.shape({
    validate: PropTypes.func,
    format: PropTypes.func,
    normalize: PropTypes.func
  }),
  name: PropTypes.string,
  id: PropTypes.string,
  className: PropTypes.string,
  showValidation: PropTypes.bool,
  validateOnChange: PropTypes.bool,
  validateAfterLength: PropTypes.number,
  isInvalid: PropTypes.bool,
  externalErrorMessage: PropTypes.string
};

export default ValidatedInput; 